import React, { Component, ErrorInfo, ReactNode } from 'react';

import { Button, Text } from '@chakra-ui/react';
import { Alert } from '@firehydrant/design-system';

import {
  isChunkLoadMessage,
  reloadAfterHealingCache,
} from 'helpers/chunkErrorHandler';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary that intercepts chunk loading failures and shows a targeted
 * "please reload" UI instead of the generic error screen.
 *
 * Chunk load errors are CDN/deployment noise, not actionable bugs — this
 * boundary explicitly does NOT report them to Bugsnag. Non-chunk errors are
 * rethrown from render() so the parent Bugsnag ErrorBoundary can catch them.
 *
 * Must be a class component because React error boundaries require
 * getDerivedStateFromError / componentDidCatch lifecycle methods.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadMessage(error)) {
      console.warn(
        '[ChunkErrorBoundary] Caught chunk load error:',
        error.message,
      );
    }
    // Non-chunk errors: no Bugsnag call here — the parent ErrorBoundary handles it.
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    if (isChunkLoadMessage(error)) {
      return <ChunkErrorFallback />;
    }

    // Non-chunk error: rethrow from render() so the parent Bugsnag ErrorBoundary
    // can catch and report it. Throwing from render() propagates to the next
    // error boundary up the tree.
    throw error;
  }
}

function ChunkErrorFallback() {
  const handleReload = () => {
    reloadAfterHealingCache();
  };

  return (
    <Alert variant="info">
      <Text as="p" fontWeight="bold">
        FireHydrant was just updated.
      </Text>
      <Text as="p">Please reload the page to get the latest version.</Text>
      <Button colorScheme="blue" mt={3} onClick={handleReload}>
        Reload now
      </Button>
    </Alert>
  );
}
